import { throttle } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DRAWER_WIDTH, HEIGHT_ITEM } from '../const';
import { changeItemsForMoving, changePercentageItem } from '../gantChangeData';
import { getMappingIdIndex } from '../gantTransformData';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantDraggable } from '../store/slices/gantDraggableSlice';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setMappingItemIdIndex, setterGant } from '../store/slices/gantSlice';
import { DraggableGantType, HeaderType, ItemType, MappingItemIdIndexType } from '../types';

const useDraggable = (
    gridGantRef: React.RefObject<HTMLDivElement>,
    headerLeftGantRef: React.RefObject<HTMLDivElement>,
) => {
    const dispatch = useDispatch<ComponentsDispatch>();

    const dragging = useSelector<RootState, DraggableGantType>((state) => state.gantDraggable.dragging);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const currentGantItem = useSelector<RootState, ItemType | null>((state) => state.gantItems.currentGantItem);
    const isItemEdit = useSelector<RootState, boolean>((state) => state.gantItems.isItemEdit);
    const currentItemVerticalDraggable = useSelector<RootState, ItemType | null>(
        (state) => state.gantDraggable.currentItemVerticalDraggable,
    );
    const currentScrollX = useSelector<RootState, number>((state) => state.gantScroll.currentScrollX);
    const currentScrollY = useSelector<RootState, number>((state) => state.gantScroll.currentScrollY);
    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);

    const oldItemIndex = useRef(0);
    const oldDayOffset = useRef(0);
    const oldPixelOffsetX = useRef(0);
    const oldPixelOffsetY = useRef(0);
    const oldPixelOffsetVertical = useRef(0);
    const oldCurrentItem = useRef<ItemType | null>(null);
    const oldCurrentItemVerticalDraggable = useRef<ItemType | null>(null);
    const oldItems = useRef<ItemType[]>([]);

    const itemIndex = useRef(0);
    const daysOffset = useRef(0);
    const pixelOffsetX = useRef(0);
    const pixelOffsetY = useRef(0);
    const pixelOffsetVertical = useRef(0);
    const [movingLeftHeaderIndex, setMovingLeftHeaderIndex] = useState(0);
    const [movingMainGantIndex, setMovingMainGantIndex] = useState(0);

    const boundaryGant = gridGantRef.current?.getBoundingClientRect();
    const boundaryHeaderGant = headerLeftGantRef.current?.getBoundingClientRect();

    useEffect(() => {
        oldCurrentItemVerticalDraggable.current = currentItemVerticalDraggable;
    }, [currentItemVerticalDraggable]);

    useEffect(() => {
        if (movingMainGantIndex % 2 || !oldCurrentItem.current || dragging === 'percentage') {
            return;
        }
        const diffDay = daysOffset.current - oldDayOffset.current;

        const newItems = changeItemsForMoving(
            mappingItemIdIndex,
            diffDay,
            dragging,
            oldCurrentItem.current!,
            oldItems.current,
            [],
        );
        const newMappingScheduleItemIdIndex = getMappingIdIndex(newItems);
        dispatch(
            setterGantItems({
                itemsList: newItems,
            }),
        );

        // dispatch(
        //     setterGant({
        //         countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
        //     }),
        // );
        dispatch(setMappingItemIdIndex(newMappingScheduleItemIdIndex));
    }, [movingMainGantIndex]);

    useEffect(() => {
        if (movingMainGantIndex % 2 || !oldCurrentItem.current || dragging !== 'percentage') {
            return;
        }

        const diffPixel = pixelOffsetX.current - oldPixelOffsetX.current;
        const newItems = changePercentageItem(
            mappingItemIdIndex,
            diffPixel,
            oldCurrentItem.current!,
            oldItems.current,
            widthColumn,
        );
        const newMappingScheduleItemIdIndex = getMappingIdIndex(newItems);

        dispatch(
            setterGantItems({
                itemsList: newItems,
            }),
        );

        dispatch(
            setterGant({
                // countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
                mappingItemIdIndex: newMappingScheduleItemIdIndex,
            }),
        );
    }, [movingMainGantIndex]);

    useEffect(() => {
        if (!oldCurrentItemVerticalDraggable.current) {
            return;
        }

        dispatch(setterGant({ pixelOffsetVertical: pixelOffsetVertical.current }));
    }, [movingLeftHeaderIndex, currentItemVerticalDraggable]);

    useEffect(() => {
        if (dragging === 'bondLeft' || dragging === 'bondRight') {
            dispatch(
                setterGantDraggable({
                    currentPositionX: pixelOffsetX.current,
                    currentPositionY: pixelOffsetY.current,
                }),
            );
        }

        if (oldCurrentItem.current) {
            dispatch(setterGantDraggable({ isDragging: true }));
        }
    }, [movingMainGantIndex]);

    const checkBoundary = (e: MouseEvent, bound?: DOMRect) => {
        const openWidowWidth = isItemEdit ? DRAWER_WIDTH : 0;
        return (
            bound?.left! < e.clientX &&
            e.clientX < bound?.right! - openWidowWidth &&
            bound?.top! < e.clientY &&
            e.clientY < bound?.bottom!
        );
    };

    const handleMouseMoveThrottled = useCallback(
        throttle(
            (
                e: MouseEvent,
                currentScrollY,
                currentScrollX,
                widthColumn,
                HEIGHT_ITEM,
                pixelOffsetVertical,
                pixelOffsetX,
                pixelOffsetY,
                daysOffset,
                itemIndex,
                setMovingLeftHeaderIndex: React.Dispatch<React.SetStateAction<number>>,
                setMovingMainGantIndex: React.Dispatch<React.SetStateAction<number>>,
                checkBoundary,
                boundaryHeaderGant,
                boundaryGant,
            ) => {
                const offsetHeightScroll = currentScrollY + e.offsetY;

                if (checkBoundary(e, boundaryHeaderGant)) {
                    pixelOffsetVertical.current = offsetHeightScroll;
                    setMovingLeftHeaderIndex((oldMovingIndicate) => oldMovingIndicate + 1);
                }

                if (checkBoundary(e, boundaryGant)) {
                    const offsetWithScroll = currentScrollX + e.offsetX;

                    pixelOffsetX.current = offsetWithScroll;
                    pixelOffsetY.current = offsetHeightScroll;
                    daysOffset.current = Math.ceil(offsetWithScroll / widthColumn);
                    itemIndex.current = Math.floor(e.offsetY / HEIGHT_ITEM);

                    setMovingMainGantIndex((oldMovingIndicate) => oldMovingIndicate + 1);
                }
            },
            30,
        ),
        [],
    );

    const handleMouseMove = (e: MouseEvent) => {
        handleMouseMoveThrottled(
            e,
            currentScrollY,
            currentScrollX,
            widthColumn,
            HEIGHT_ITEM,
            pixelOffsetVertical,
            pixelOffsetX,
            pixelOffsetY,
            daysOffset,
            itemIndex,
            setMovingLeftHeaderIndex,
            setMovingMainGantIndex,
            checkBoundary,
            boundaryHeaderGant,
            boundaryGant,
        );
    };

    // const handleMouseMove = (e: MouseEvent) => {
    //     console.log('++++');
    //
    //     const offsetHeightScroll = currentScrollY + e.offsetY;
    //
    //     if (checkBoundary(e, boundaryHeaderGant)) {
    //         pixelOffsetVertical.current = offsetHeightScroll;
    //         setMovingLeftHeaderIndex((oldMovingIndicate) => oldMovingIndicate + 1);
    //     }
    //
    //     if (checkBoundary(e, boundaryGant)) {
    //         const offsetWithScroll = currentScrollX + e.offsetX;
    //
    //         pixelOffsetX.current = offsetWithScroll;
    //         pixelOffsetY.current = offsetHeightScroll;
    //         daysOffset.current = Math.ceil(offsetWithScroll / widthColumn);
    //         itemIndex.current = Math.floor(e.offsetY / HEIGHT_ITEM);
    //
    //         setMovingMainGantIndex((oldMovingIndicate) => oldMovingIndicate + 1);
    //     }
    // };

    const handleMouseUp = () => {
        //todo need for follow. this row was add to fix issue for displaying current item when we try edit and open form
        // but now this work properly without this row
        // dispatch(setterGant({dragging: 'none', currentItem: isItemEdit ? currentItem : null}));
        dispatch(
            setterGantDraggable({
                dragging: 'none',
            }),
        );

        setTimeout(() => {
            dispatch(
                setterGantDraggable({
                    isDragging: false,
                    currentItemVerticalDraggable: null,
                    currentDropBond: null,
                    currentDragBond: null,
                    currentPreDropBond: null,
                }),
            );
        });
        oldPixelOffsetX.current = 0;
        oldPixelOffsetY.current = 0;
        oldPixelOffsetVertical.current = 0;
        oldDayOffset.current = 0;
        oldItemIndex.current = 0;
        oldCurrentItem.current = null;
        oldCurrentItemVerticalDraggable.current = null;
        oldItems.current = [];
    };

    const handleMouseDown = (e: MouseEvent) => {
        if (e.button === 2) {
            return;
        }

        oldPixelOffsetX.current = pixelOffsetX.current;
        oldPixelOffsetY.current = pixelOffsetY.current;
        oldPixelOffsetVertical.current = pixelOffsetVertical.current;
        pixelOffsetVertical.current = pixelOffsetVertical.current + 1;
        oldDayOffset.current = daysOffset.current;
        oldItemIndex.current = itemIndex.current;
        oldCurrentItem.current = currentGantItem;
        oldItems.current = [...itemsList];
        if (checkBoundary(e, boundaryHeaderGant)) {
            dispatch(setterGantItems({ isItemEdit: false, currentGantItem: null }));
        }
        dispatch(
            setterGantDraggable({
                currentPositionX: pixelOffsetX.current, //e.offsetX + currentScrollX,
                currentPositionY: pixelOffsetY.current, //e.offsetY + currentScrollY,
            }),
        );
    };

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mousedown', handleMouseDown);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mousedown', handleMouseDown);
        };
    }, [
        dragging,
        currentScrollX,
        currentScrollY,
        headerType,
        currentGantItem,
        currentItemVerticalDraggable,
        widthColumn,
        boundaryGant,
    ]);
};

export default useDraggable;
